import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link, Navigate, useParams } from 'react-router-dom';
import { BlogCard, BlogImage } from '../components/BlogCard';
import { CTASection } from '../components/CTASection';
import { Container, Section, SectionHeading, Reveal } from '../components/Section';
import { getPostBySlug, getRelatedPosts } from '../lib/blog';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';
import { site } from '../data/site';

export function BlogPost() {
  const { slug } = useParams();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post);
  const articleUrl = `${site.url}/blog/${post.slug}`;

  return (
    <>
      <SEO
        title={post.title}
        description={post.summary}
        type="article"
        canonical={articleUrl}
        keywords={[post.category, ...post.tags, ...site.keywords]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.summary,
          datePublished: post.date,
          author: {
            '@type': 'Organization',
            name: site.name
          },
          publisher: {
            '@type': 'Organization',
            name: site.name,
            logo: {
              '@type': 'ImageObject',
              url: `${site.url}/favicon.svg`
            }
          },
          mainEntityOfPage: articleUrl
        }}
      />

      <article className="border-b border-brand-border bg-white">
        <Container className="py-16 sm:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy">
            <Icon name="arrowRight" className="h-4 w-4 rotate-180" /> Back to blog
          </Link>
          <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_.75fr] lg:items-center">
            <div>
              <p className="text-sm font-extrabold uppercase tracking-[0.14em] text-brand-teal">{post.category}</p>
              <h1 className="mt-4 font-display text-5xl leading-tight text-brand-dark sm:text-6xl">{post.title}</h1>
              <p className="mt-6 text-lg leading-8 text-brand-muted">{post.summary}</p>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-sm font-bold text-brand-muted">
                <span>{new Date(`${post.date}T00:00:00`).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}</span>
                <span className="h-1 w-1 rounded-full bg-brand-border" />
                <span>{post.readTime}</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-brand-background px-3 py-1 text-xs font-bold text-brand-navy">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <BlogImage image={post.image} title={post.title} />
          </div>
        </Container>
      </article>

      <Section>
        <div className="mx-auto max-w-3xl rounded-lg border border-brand-border bg-white p-6 shadow-card sm:p-10">
          <ReactMarkdown className="prose-brand max-w-none" remarkPlugins={[remarkGfm]}>
            {post.content}
          </ReactMarkdown>
        </div>
      </Section>

      <Section className="bg-white">
        <SectionHeading eyebrow="Related" title="Keep reading." />
        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {related.map((item) => (
            <Reveal key={item.slug}>
              <BlogCard post={item} />
            </Reveal>
          ))}
        </div>
      </Section>

      <CTASection title="Have a workflow, network, or hosting question behind this article?" button="Ask MaytoMation" />
    </>
  );
}
